import type { AlertColor, SxProps } from '@mui/material';
import type { materialTheme } from '@/theme';

import { createContext, useContext } from 'react';

export type ToastConfig = {
  vertical?: 'bottom' | 'top';
  horizontal?: 'center' | 'left' | 'right';
  sx?: ThemeSx;
  snackbarSx?: ThemeSx;
};

type Severity = {
  severity: AlertColor;
};

export interface ToastMessage {
  message: string;
  severity: AlertColor;
  key: number;
  toastConfig?: ToastConfig;
}

export type ThemeSx = SxProps<typeof materialTheme>;

export const ToastContext = createContext<{
  addMessage: (message: ToastMessage, toastConfig?: ToastConfig) => void;
}>(null as never);

export const useToast = () => {
  const { addMessage } = useContext(ToastContext);

  const show = (message: string, options: ToastConfig & Severity) => {
    const { severity, ...toastConfig } = options;
    addMessage({ message, severity, toastConfig, key: Date.now() });
  };

  return {
    show,
    info(message: string) {
      show(message, { severity: 'info' });
    },
    success(message: string) {
      show(message, { severity: 'success' });
    },
    ok(message: string, config?: ToastConfig) {
      const { snackbarSx = {}, sx = {}, ...rest } = config || {};

      show(message, {
        ...rest,
        severity: 'success',
        snackbarSx: {
          width: 'max-content',
          '.MuiAlert-icon': {
            alignItems: 'center',
            paddingTop: 0,
            paddingBottom: 0,
          },
          '.MuiAlert-action': {
            alignItems: 'center',
            paddingTop: 0,
            paddingBottom: 0,
            marginTop: 0,
          },
          ...snackbarSx,
        },
        sx: {
          // Alert content styling
          fontSize: '16px',
          lineHeight: '26px',
          width: 'max-content',
          wordWrap: 'break-word',
          whiteSpace: 'normal',
          ...sx,
        },
      });
    },
    warning(message: string) {
      show(message, { severity: 'warning' });
    },
    error(message: string) {
      show(message, { severity: 'error' });
    },
  };
};
